import React, { useState } from 'react';
import { X, Clock, Search, Download, BarChart, Bell, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/apiClient';
import { useParams } from 'react-router-dom';
import { createAlarm } from '../../../actions/alarms';

const LogQueryModal = ({ isOpen, onClose, darkMode, logGroupName, serviceId }) => {
  const [mode, setMode] = useState('query'); // 'query' or 'alarm'
  const [queryData, setQueryData] = useState({
    query: '',
    timeRange: {
      type: 'relative',
      duration: 1,
      unitOfTime: 'hours'
    }
  });
  
  const [queryResults, setQueryResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [lastQueryTime, setLastQueryTime] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [columns, setColumns] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [conditionLogic, setConditionLogic] = useState('&&');
  const [alarmFormData, setAlarmFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    processingPeriod: {
      duration: 5,
      unit: 'minutes'
    },
  });
  
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { projectId } = useParams();

  // Sample query templates
  const queryTemplates = [
    { 
      name: "Error Count", 
      query: "fields @timestamp, @message\n| filter @message like /ERROR/\n| stats count(*) by bin(30m)" 
    },
    { 
      name: "Latency Analysis", 
      query: "fields @timestamp, @message\n| filter @message like /latency/\n| parse @message /latency: (?<latency>\\d+)/ \n| stats avg(latency) as avg_latency, max(latency) as max_latency by bin(5m)" 
    },
    { 
      name: "HTTP Status Codes", 
      query: "fields @timestamp, @message\n| filter @message like /status code/\n| parse @message /status code: (?<status>\\d+)/ \n| stats count(*) as count by status" 
    },
    {
      name: "API Calls Success/Failure",
      query: "filter @message like /HTTP\\/1\\.1/\n| stats \n    count(*) as totalRequests,\n    count(@message like / 200 -/) as successfulRequests,\n    count(@message like / [^2]\\d\\d -/) as failedRequests"
    },
    {
      name: "Specific API Analysis",
      query: "fields @timestamp, @message\n| filter @message like /lendermatrix/\n| stats count() as hit_count"
    }
  ];

  const operators = [
    { value: '>', label: 'Greater than (>)' },
    { value: '<', label: 'Less than (<)' },
    { value: '==', label: 'Equal to (==)' },
    { value: '!=', label: 'Not equal to (!=)' },
    { value: '>=', label: 'Greater than or equal (>=)' },
    { value: '<=', label: 'Less than or equal (<=)' }
  ];

  const addCondition = () => {
    setConditions([...conditions, { column: '', operator: '>', value: '' }]);
  };

  const removeCondition = (index) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    newConditions[index][field] = value;
    setConditions(newConditions);
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    setLoading(true);
    setError(null);
    setShowResults(true);

    try {
      let timeRangeParam = {};
      
      if (queryData.timeRange.type === 'relative') {
        timeRangeParam = {
          start: `${queryData.timeRange.duration}${queryData.timeRange.unitOfTime.charAt(0)}`,
        };
      } else {
        timeRangeParam = {
          start: queryData.timeRange.startDate,
          end: queryData.timeRange.endDate
        };
      }

      const data = await apiClient.post('/cloudwatch-log-insights',
        {
          logGroupName,
          query: queryData.query,
          timeRange: timeRangeParam
        },
        {
          projectId,
          resourceId: serviceId,
          resourceType: 'BACKEND_SERVICE',
          requiredPermission: 'run:cw_log_insight_query'
        }
      );
      
      if (data.results && data.results.length > 0) {
        const firstResult = data.results[0];
        const extractedColumns = Object.keys(typeof firstResult === 'object' ? firstResult : {});
        setColumns(extractedColumns.length > 0 ? extractedColumns : ['value']);
      }
      
      setQueryResults(data.results || []);
      setLastQueryTime(new Date());
    } catch (error) {
      console.error('Error executing CloudWatch query:', error);
      setError('Failed to execute query. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlarm = () => {
    if (!alarmFormData.name || !queryData.query) {
      setError('Alarm name and query are required');
      return;
    }

    const alarmData = {
      ...alarmFormData,
      query: queryData.query,
      conditions,
      conditionLogic,
      logGroupName,
      serviceId
    };

    dispatch(createAlarm(alarmData, projectId, serviceId));
    onClose();
  };

  const downloadResults = () => {
    if (viewMode === 'table') {
      let csvContent = '';
      
      if (columns.length > 0) {
        csvContent += columns.join(',') + '\n';
      }
      
      queryResults.forEach(result => {
        if (typeof result === 'object') {
          const row = columns.map(col => {
            const value = result[col];
            return value !== undefined ? `"${value}"` : '""';
          }).join(',');
          csvContent += row + '\n';
        } else {
          csvContent += `"${result}"\n`;
        }
      });
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cloudwatch-query-${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCancel = () => {
    setQueryData({
      query: '',
      timeRange: {
        type: 'relative',
        duration: 1,
        unitOfTime: 'hours'
      }
    });
    setQueryResults([]);
    setShowResults(false);
    setMode('query');
    onClose();
  };

  const applyTemplate = (template) => {
    setQueryData({
      ...queryData,
      query: template.query
    });
  };

  const renderTableResults = () => {
    if (!queryResults.length) return null;

    const isSimpleArray = typeof queryResults[0] === 'string';

    if (isSimpleArray) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  #
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {queryResults.map((result, index) => (
                <tr 
                  key={index}
                  className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}
                >
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                    {index + 1}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 font-mono">
                    {result}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                #
              </th>
              {columns.map((column, colIndex) => (
                <th 
                  key={colIndex} 
                  scope="col" 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {queryResults.map((result, index) => (
              <tr 
                key={index}
                className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}
              >
                <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                  <button className="focus:outline-none">
                    {index + 1}
                  </button>
                </td>
                {columns.map((column, colIndex) => (
                  <td 
                    key={`${index}-${colIndex}`} 
                    className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 font-mono"
                  >
                    {typeof result === 'object' ? 
                      (result[column] !== undefined ? result[column].toString() : '') : 
                      result.toString()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChartResults = () => {
    if (!queryResults.length) return null;

    const hasNumericData = queryResults.some(result => 
      typeof result === 'object' && 
      Object.values(result).some(value => !isNaN(Number(value)))
    );

    if (!hasNumericData) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <p>No numeric data available for charting</p>
          <button 
            onClick={() => setViewMode('table')}
            className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
          >
            View as Table
          </button>
        </div>
      );
    }

    return (
      <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-md p-4 flex flex-col items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400 flex items-center mb-4">
          <BarChart className="mr-2" />
          <span>Chart Visualization Placeholder</span>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          (In a real implementation, this would be an actual chart rendered with Recharts or Chart.js)
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"></div>
  
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-5xl relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-900 shadow-xl transition-all">
          <div className="bg-gray-900 dark:bg-gray-800 px-4 py-3 flex justify-between items-center sticky top-0 z-10">
            <h3 className="text-lg font-medium text-white flex items-center">
              {mode === 'query' ? (
                <>
                  <Search className="mr-2 h-5 w-5" />
                  CloudWatch Insights Query
                </>
              ) : (
                <>
                  <AlertCircle className="mr-2 h-5 w-5" />
                  Create Alarm
                </>
              )}
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
  
          <div className="p-6 overflow-y-auto max-h-[70vh] bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            {/* Mode Toggle */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  onClick={() => setMode('query')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                    mode === 'query'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Run Query
                </button>
                <button
                  onClick={() => setMode('alarm')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                    mode === 'alarm'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Create Alarm
                </button>
              </div>
            </div>
  
            {mode === 'alarm' && (
              <div className="mb-6 space-y-4">
                <div>
                  <label className="text-gray-700 dark:text-gray-300 text-sm font-medium block mb-1">
                    Alarm Name
                  </label>
                  <input
                    type="text"
                    value={alarmFormData.name}
                    onChange={(e) => setAlarmFormData({...alarmFormData, name: e.target.value})}
                    placeholder="Enter alarm name"
                    className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
  
                <div>
                  <label className="text-gray-700 dark:text-gray-300 text-sm font-medium block mb-1">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={alarmFormData.description}
                    onChange={(e) => setAlarmFormData({...alarmFormData, description: e.target.value})}
                    placeholder="Enter alarm description"
                    className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
  
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <label className="text-sm font-medium block mb-3 flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    Processing Period
                  </label>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Duration</label>
                      <input
                        type="number"
                        min="1"
                        value={alarmFormData.processingPeriod.duration}
                        onChange={(e) => setAlarmFormData({
                          ...alarmFormData,
                          processingPeriod: {
                            ...alarmFormData.processingPeriod,
                            duration: parseInt(e.target.value) || 1
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Unit</label>
                      <select
                        value={alarmFormData.processingPeriod.unit}
                        onChange={(e) => setAlarmFormData({
                          ...alarmFormData,
                          processingPeriod: {
                            ...alarmFormData.processingPeriod,
                            unit: e.target.value
                          }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    How far back to analyze logs each time the alarm runs
                  </p>
                </div>
              </div>
            )}
  
            {mode === 'query' && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <label className="text-sm font-medium block mb-3 flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  Time Range
                </label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`
                    p-3 rounded-md cursor-pointer transition-all
                    ${queryData.timeRange.type === 'relative' 
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-800' 
                      : 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}
                  `}>
                    <div className="flex items-center mb-2">
                      <input
                        type="radio"
                        id="relativeTime"
                        name="timeRangeType"
                        checked={queryData.timeRange.type === 'relative'}
                        onChange={() => setQueryData({
                          ...queryData,
                          timeRange: {
                            ...queryData.timeRange,
                            type: 'relative'
                          }
                        })}
                        className="mr-2"
                      />
                      <label htmlFor="relativeTime" className="text-sm font-medium cursor-pointer dark:text-gray-300">
                        Relative
                      </label>
                    </div>
                    
                    <div className="flex items-center mt-2 pl-6">
                      <div className="grid grid-cols-2 gap-2 w-full">
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Duration</label>
                          <input
                            type="number"
                            min="1"
                            value={queryData.timeRange.duration}
                            onChange={(e) => setQueryData({
                              ...queryData,
                              timeRange: {
                                ...queryData.timeRange,
                                duration: e.target.value
                              }
                            })}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Unit</label>
                          <select
                            value={queryData.timeRange.unitOfTime}
                            onChange={(e) => setQueryData({
                              ...queryData,
                              timeRange: {
                                ...queryData.timeRange,
                                unitOfTime: e.target.value
                              }
                            })}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`
                    p-3 rounded-md cursor-pointer transition-all
                    ${queryData.timeRange.type === 'absolute' 
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-800' 
                      : 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}
                  `}>
                    <div className="flex items-center mb-2">
                      <input
                        type="radio"
                        id="absoluteTime"
                        name="timeRangeType"
                        checked={queryData.timeRange.type === 'absolute'}
                        onChange={() => setQueryData({
                          ...queryData,
                          timeRange: {
                            ...queryData.timeRange,
                            type: 'absolute',
                            startDate: new Date(Date.now() - 3600000).toISOString(),
                            endDate: new Date().toISOString()
                          }
                        })}
                        className="mr-2"
                      />
                      <label htmlFor="absoluteTime" className="text-sm font-medium cursor-pointer dark:text-gray-300">
                        Absolute
                      </label>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-2 pl-6">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Start Date</label>
                          <input
                            type="datetime-local"
                            value={queryData.timeRange.startDate ? new Date(queryData.timeRange.startDate).toISOString().slice(0, 16) : ''}
                            onChange={(e) => setQueryData({
                              ...queryData,
                              timeRange: {
                                ...queryData.timeRange,
                                startDate: new Date(e.target.value).toISOString()
                              }
                            })}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">End Date</label>
                          <input
                            type="datetime-local"
                            value={queryData.timeRange.endDate ? new Date(queryData.timeRange.endDate).toISOString().slice(0, 16) : ''}
                            onChange={(e) => setQueryData({
                              ...queryData,
                              timeRange: {
                                ...queryData.timeRange,
                                endDate: new Date(e.target.value).toISOString()
                              }
                            })}
                            className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
  
            {/* Query Templates */}
            <div className="mt-4">
              <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">
                Query Templates
              </label>
              <div className="flex flex-wrap gap-2">
                {queryTemplates.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => applyTemplate(template)}
                    className="px-3 py-1 text-xs rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>
  
            {/* Query Input */}
            <div className="mt-4">
              <label className="text-sm font-medium block mb-1 text-gray-700 dark:text-gray-300">
                CloudWatch Insights Query
              </label>
              <textarea
                value={queryData.query}
                onChange={(e) => setQueryData({ ...queryData, query: e.target.value })}
                rows={8}
                className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm h-64 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white"
                placeholder="fields @timestamp, @message | filter @message like /ERROR/ | stats count(*) by bin(30m)"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Write your CloudWatch Insights query using the query syntax.
              </p>
            </div>
  
            {/* Conditions Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mt-4">
              <label className="text-sm font-medium block mb-3 flex items-center">
                <Bell className="h-4 w-4 mr-2 text-gray-500" />
                Alert Conditions
              </label>
              
              {conditions.length > 0 && (
                <div className="mb-3">
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Condition Logic</label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="&&"
                        checked={conditionLogic === '&&'}
                        onChange={() => setConditionLogic('&&')}
                        className="mr-2"
                      />
                      <span className="text-sm">ALL conditions must be met (AND)</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="||"
                        checked={conditionLogic === '||'}
                        onChange={() => setConditionLogic('||')}
                        className="mr-2"
                      />
                      <span className="text-sm">ANY condition must be met (OR)</span>
                    </label>
                  </div>
                </div>
              )}
              
              {conditions.map((condition, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-4">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Column</label>
                    <input
                      type="text"
                      value={condition.column}
                      onChange={(e) => updateCondition(index, 'column', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  
                  <div className="col-span-3">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Operator</label>
                    <select
                      value={condition.operator}
                      onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      {operators.map((op, opIndex) => (
                        <option key={opIndex} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-span-4">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Value</label>
                    <input
                      type="text"
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Value to compare"
                    />
                  </div>
                  
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeCondition(index)}
                      className="p-2 text-red-500 hover:text-red-700 focus:outline-none"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={addCondition}
                className="mt-2 inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Condition
              </button>
            </div>
  
            {/* Query Results Section */}
            {showResults && mode === 'query' && (
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <h3 className="font-medium text-gray-800 dark:text-gray-200">Query Results</h3>
                    {lastQueryTime && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {lastQueryTime.toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setViewMode('table')}
                        className={`px-3 py-1 text-xs font-medium ${
                          viewMode === 'table' 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200' 
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Table
                      </button>
                      <button
                        onClick={() => setViewMode('chart')}
                        className={`px-3 py-1 text-xs font-medium ${
                          viewMode === 'chart' 
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200' 
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Chart
                      </button>
                    </div>
                    
                    <button
                      onClick={downloadResults}
                      disabled={queryResults.length === 0 || loading}
                      className={`p-2 rounded-md transition-all duration-200
                        bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 
                        border border-gray-200 dark:border-gray-700 
                        hover:bg-gray-50 dark:hover:bg-gray-700
                        focus:outline-none focus:ring-1 focus:ring-blue-500
                        ${(queryResults.length === 0 || loading) ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      title="Download results"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                </div>
  
                <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {error ? (
                    <div className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 border rounded-lg">
                      <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                      <button
                        onClick={handleSubmit}
                        className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Retry Query
                      </button>
                    </div>
                  ) : loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
                    </div>
                  ) : queryResults.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">No results found</p>
                      <p className="text-xs mt-1">Try adjusting your query or time range settings</p>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 overflow-hidden">
                      {viewMode === 'table' ? renderTableResults() : renderChartResults()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
  
          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Close
              </button>
              {mode === 'query' ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !queryData.query.trim()}
                  className={`inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    loading || !queryData.query.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? 'Running Query...' : 'Run Query'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreateAlarm}
                  disabled={!alarmFormData.name || !queryData.query.trim()}
                  className={`inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    !alarmFormData.name || !queryData.query.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Create Alarm
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogQueryModal;

